<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { ContractManagementColumnExtractionScope } from '@/api';
import GaiaChoiceCard from '@/components/design/base/GaiaChoiceCard.vue';

/** Asks how much of the existing backlog a changed question re-extracts. */
const props = withDefaults(
  defineProps<{
    show: boolean;
    loading?: boolean;
    /** The question belongs to a prompt that columns of several views may ask. */
    acrossViews?: boolean;
  }>(),
  { loading: false, acrossViews: false }
);

const emit = defineEmits<{
  'update:show': [value: boolean];
  confirm: [scope: ContractManagementColumnExtractionScope];
}>();

const showModal = computed({
  get: () => props.show,
  set: (value: boolean) => {
    emit('update:show', value);
  },
});

const extractionScope = ref<ContractManagementColumnExtractionScope>(
  ContractManagementColumnExtractionScope.NEW_ONLY
);

watch(
  () => props.show,
  show => {
    if (show) extractionScope.value = ContractManagementColumnExtractionScope.NEW_ONLY;
  }
);

const extractionScopeOptions = computed(
  (): {
    value: ContractManagementColumnExtractionScope;
    label: string;
    description: string;
  }[] => {
    const where = props.acrossViews ? 'each view' : 'this view';
    return [
      {
        value: ContractManagementColumnExtractionScope.NEW_ONLY,
        label: 'Only new contracts',
        description:
          'Existing rows keep their current values. The new prompt is used for contracts added from now on.',
      },
      {
        value: ContractManagementColumnExtractionScope.LAST_5,
        label: 'Last 5 contracts',
        description: `Re-extract the 5 newest contracts in ${where}, plus all future ones.`,
      },
      {
        value: ContractManagementColumnExtractionScope.LAST_10,
        label: 'Last 10 contracts',
        description: `Re-extract the 10 newest contracts in ${where}, plus all future ones.`,
      },
      {
        value: ContractManagementColumnExtractionScope.ALL,
        label: props.acrossViews ? 'All contracts in each view' : 'All contracts in this view',
        description: 'Re-extract every contract. Takes the longest and consumes the most credits.',
      },
    ];
  }
);
</script>

<template>
  <n-modal
    v-model:show="showModal"
    preset="card"
    title="Re-run extractions?"
    :mask-closable="false"
    style="width: 520px"
  >
    <n-flex vertical :size="16">
      <n-text depth="3">
        <slot>
          You changed this column's question. Which contracts should be re-extracted with the new
          prompt?
        </slot>
      </n-text>

      <n-flex vertical :size="8" :wrap="false">
        <gaia-choice-card
          v-for="option in extractionScopeOptions"
          :key="option.value"
          :label="option.label"
          :meta="option.description"
          :selected="extractionScope === option.value"
          @select="extractionScope = option.value"
        />
      </n-flex>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <n-button @click="showModal = false">Cancel</n-button>
        <n-button type="primary" :loading="loading" @click="emit('confirm', extractionScope)">
          Save
        </n-button>
      </n-flex>
    </template>
  </n-modal>
</template>
